import {
    AlignItems,
    Card,
    Column,
    Container,
    Gap,
    Gutter,
    JustifyContent,
    Row,
    RowCols,
    Stack,
    StackDirection,
    StackWrap,
    Text,
    Wrapper
} from "@rebootui";
import Icon from "../common/Icon";



const steps = [
    { number: "01", label: "Install", desc: "Add the package via npm or yarn" },
    { number: "02", label: "Import CSS", desc: "Include the stylesheet in your entry file" },
    { number: "03", label: "Start Building", desc: "Import components and build your UI" },
];

const features = [
    { icon: "check", label: "TypeScript Ready" },
    { icon: "check", label: "React 18+" },
    { icon: "check", label: "Bootstrap 5" },
    { icon: "check", label: "Tree-Shakeable" },
    { icon: "check", label: "Accessible" },
    { icon: "check", label: "Customizable" },
];

const GetStarted = () => {
    return (
        <section className="py-md-7 py-6">
            <Container>
                <Stack direction={StackDirection.Column} gap={Gap.Gap5}>
                    <Wrapper className="text-center">
                        <Text className="h2 mb-3">
                            Get Started in Minutes
                        </Text>
                        <Text className="fs-5 mb-0 text-secondary">
                            Install via npm and import the CSS — you're ready in seconds.
                        </Text>
                    </Wrapper>
                    <Row cols={[RowCols.RowColsMd3, RowCols.RowColsSm6]} gutter={Gutter.G4}>
                        {steps.map((step, index) => (
                            <Column key={index}>
                                <Stack alignItems={AlignItems.Start} gap={Gap.Gap3} className="p-3 rounded-4 border border-black border-opacity-10 bg-white shadow-s h-100">
                                    <Stack alignItems={AlignItems.Center} justifyContent={JustifyContent.Center} className="rounded-3" style={{ width: 36, height: 36, background: "linear-gradient(135deg,#6366f1,#8b5cf6)" }}>
                                        <Text el="p" className="mb-0 text-white fs-xs fw-bold">{step.number}</Text>
                                    </Stack>
                                    <Stack.Item>
                                        <Text className="h6 mb-1">{step.label}</Text>
                                        <Text el="p" className="mb-0 text-muted fs-xs">{step.desc}</Text>
                                    </Stack.Item>
                                </Stack>
                            </Column>
                        ))}
                    </Row>
                    <Row cols={[RowCols.RowColsMd2, RowCols.RowCols1]} gutter={Gutter.G4}>
                        <Column>
                            <div className="position-relative zi-99">
                                <div className="position-absolute top-0 start-0 w-100 blur-box lg-pink-blue" />
                                <Card className="rounded-4 border border-black border-opacity-10 shadow-l">
                                    <Card.Header className="rounded-4 rounded-bottom-0 px-4 py-m lg-pink-blue">
                                        <Stack alignItems={AlignItems.Center} gap={Gap.Gap3}>
                                            <Stack.Item>
                                                <Stack alignItems={AlignItems.Center} gap={Gap.Gap3} className="bg-white p-2 rounded-3 border border-black border-opacity-10 shadow-s">
                                                    <Icon name="cube" width={22} height={22} />
                                                </Stack>
                                            </Stack.Item>
                                            <Stack.Item>
                                                <Text className="h5 mb-1">Install via NPM</Text>
                                                <Text el="p" className="text-secondary mb-0 small">Recommended for projects</Text>
                                            </Stack.Item>
                                        </Stack>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <Stack direction={StackDirection.Column} gap={Gap.Gap3} >
                                            <Stack alignItems={AlignItems.Center} justifyContent={JustifyContent.Between} className="bg-dark px-m py-s rounded-4">
                                                <span className="text-white overflow-x-auto pe-3">
                                                    <pre className="mb-0">
                                                        <code>
                                                            npm install @reactufy/rebootui
                                                        </code>
                                                    </pre>
                                                </span>
                                                <Stack alignItems={AlignItems.Center} className="bg-white p-2 rounded-3">
                                                    <Icon name="copy" width={18} height={18} />
                                                </Stack>
                                            </Stack>
                                            <Stack alignItems={AlignItems.Center} justifyContent={JustifyContent.Between} className="bg-dark px-m py-s rounded-4">
                                                <span className="text-white overflow-x-auto pe-3">
                                                    <pre className="mb-0">
                                                        <code>
                                                            yarn add @reactufy/rebootui
                                                        </code>
                                                    </pre>
                                                </span>
                                                <Stack alignItems={AlignItems.Center} className="bg-white p-2 rounded-3">
                                                    <Icon name="copy" width={18} height={18} />
                                                </Stack>
                                            </Stack>
                                            <Text el="p" className="small text-muted mb-0">
                                                <span className="me-1">ℹ️</span>
                                                Requires React&nbsp;18+ and React DOM as peer dependencies.
                                            </Text>
                                        </Stack>
                                    </Card.Body>
                                </Card>
                            </div>
                        </Column>
                        <Column>
                            <div className="position-relative zi-99">
                                <div className="position-absolute top-0 start-0 w-100 blur-box lg-green-blue" />
                                <Card className="rounded-4 border border-black border-opacity-10 shadow-l">
                                    <Card.Header className="rounded-4 rounded-bottom-0 px-4 py-m lg-green-blue">
                                        <Stack alignItems={AlignItems.Center} gap={Gap.Gap3}>
                                            <Stack alignItems={AlignItems.Center} gap={Gap.Gap3} className="text-success bg-white p-2 rounded-3 border border-black border-opacity-10 shadow-s">
                                                <Icon name="light" width={22} height={22} />
                                            </Stack>
                                            <Stack.Item>
                                                <Text className="h5 mb-1">Import CSS &amp; Components</Text>
                                                <Text el="p" className="text-secondary mb-0 small">Add to your entry file</Text>
                                            </Stack.Item>
                                        </Stack>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <Stack direction={StackDirection.Column} gap={Gap.Gap3} >
                                            <Stack alignItems={AlignItems.Center} justifyContent={JustifyContent.Between} className="bg-dark px-m py-s rounded-4">
                                                <span className="text-white overflow-x-auto pe-3">
                                                    <pre className="mb-0">
                                                        <code>
                                                            import '@reactufy/rebootui/dist/rebootui-complete.css';
                                                        </code>
                                                    </pre>
                                                </span>
                                                <Stack alignItems={AlignItems.Center} className="bg-white p-2 rounded-3">
                                                    <Icon name="copy" width={18} height={18} />
                                                </Stack>
                                            </Stack>
                                            <Stack alignItems={AlignItems.Center} justifyContent={JustifyContent.Between} className="bg-dark px-m py-s rounded-4">
                                                <span className="text-white overflow-x-auto pe-3">
                                                    <pre className="mb-0">
                                                        <code>
                                                            import {'{'} Button {'}'} from '@reactufy/rebootui';
                                                        </code>
                                                    </pre>
                                                </span>
                                                <Stack alignItems={AlignItems.Center} className="bg-white p-2 rounded-3">
                                                    <Icon name="copy" width={18} height={18} />
                                                </Stack>
                                            </Stack>
                                            <Text el="p" className="small text-muted mb-0">
                                                <span className="me-1">💡</span>
                                                Use <code>rebootui-overrides.css</code> if Bootstrap is already in your project.
                                            </Text>
                                        </Stack>
                                    </Card.Body>
                                </Card>
                            </div>
                        </Column>
                    </Row>
                    <Stack wrap={StackWrap.Wrap} justifyContent={JustifyContent.Center} gap={Gap.Gap3}>
                        {features.map((f, i) => (
                            <Stack key={i} alignItems={AlignItems.Center} gap={Gap.Gap1} className="badge rounded-pill border border-black border-opacity-10 bg-white text-dark shadow-s px-3 py-2 fs-xs">
                                <Icon name={f.icon} width={13} height={13} />
                                {f.label}
                            </Stack>
                        ))}
                    </Stack>
                    <Stack alignItems={AlignItems.Center} justifyContent={JustifyContent.Center} gap={Gap.Gap3} wrap={StackWrap.Wrap} className="position-relative zi-99">
                        <a
                            href="/docs/get-started"
                            className="btn btn-sm btn-dark text-white lh-160 rounded-4 shadow-l"
                        >
                            Read Documentation
                        </a>
                        <a
                            href="https://www.npmjs.com/package/@reactufy/rebootui"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-dark lh-160 rounded-4"
                        >
                            View on NPM
                        </a>
                    </Stack>
                </Stack>
            </Container>
        </section>
    );
};

export default GetStarted;
